import { useEffect, useState } from "react";
import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  fetchRelatedRecords,
  InfiniteListBase,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useNotify,
  useTranslate,
  type Exporter,
} from "ra-core";
import { Link, useNavigate } from "react-router";
import { ExternalLink, FileText, Plus } from "lucide-react";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { BulkDeleteButton } from "@/components/admin/bulk-delete-button";
import { BulkExportButton } from "@/components/admin/bulk-export-button";
import { List } from "@/components/admin/list";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import type { Company, Contact, Sale, Tag } from "../types";
import { BulkAssignButton } from "./BulkAssignButton";
import { BulkTagButton } from "./BulkTagButton";
import { BulkTemperatureButton } from "./BulkTemperatureButton";
import { ContactEmpty } from "./ContactEmpty";
import { ContactFilterBar } from "./ContactFilterBar";
import { ContactImportDialog } from "./ContactImportButton";
import {
  ContactListContent,
  ContactListContentMobile,
} from "./ContactListContent";
import {
  ContactListFilterSummary,
  ContactListFilter,
} from "./ContactListFilter";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";

export const ContactList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <List
      title={false}
      actions={<ContactListActions />}
      perPage={25}
      sort={{ field: "first_seen", order: "DESC" }}
      filterDefaultValues={{ "archived_at@is": null }}
      exporter={exporter}
      disableBreadcrumb
    >
      <ContactListLayoutDesktop />
    </List>
  );
};

const ContactListLayoutDesktop = () => {
  const { data, isPending, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  // Sprint 7 T7.2.a — Sidebar verticale ContactListFilter remplacée par
  // ContactFilterBar horizontale au-dessus de la table.
  return (
    <div className="flex flex-col gap-3">
      <ContactFilterBar />
      <Card className="py-0">
        <ContactListContent />
      </Card>
      <BulkActionsToolbar>
        <ContactBulkActionButtons />
      </BulkActionsToolbar>
    </div>
  );
};

const ContactBulkActionButtons = () => (
  <>
    <SelectAllButton />
    <BulkTemperatureButton />
    <BulkAssignButton />
    <BulkTagButton />
    <BulkExportButton />
    <BulkDeleteButton />
  </>
);

// Sprint 7 T7.1.d — Toolbar conforme à design/atomic-crm/contacts-app.jsx
// Ordre : Importer CSV (label) · Exporter (icon-only) · Ajouter contact
// (pill primary + kbd N). Refresh est dans FabrikTopBar (cf maquette
// Topbar contacts-app.jsx), pas dupliqué ici. Raccourci clavier "n" →
// navigate /contacts/create (sauf si focus sur input/textarea).
const ContactListActions = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { getData, total, resource } = useListContext();
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "n" || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target?.matches?.("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
      e.preventDefault();
      navigate("/contacts/create");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const handleExport = async () => {
    if (!getData) return;
    try {
      const data = await getData({ maxResults: 1000 });
      await exporter(
        data as Contact[],
        fetchRelatedRecords(dataProvider),
        dataProvider,
        resource,
      );
    } catch (err) {
      console.error(err);
      notify("HTTP Error", { type: "error" });
    }
  };

  return (
    <TopToolbar>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 h-8 px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setImportOpen(true)}
        >
          <FileText className="size-3.5" />
          {translate("resources.contacts.import.button", { _: "Importer CSV" })}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={handleExport}
          disabled={!total}
          title="Exporter"
          aria-label="Exporter"
        >
          <ExternalLink className="size-4" />
        </Button>
        <Button
          asChild
          type="button"
          size="sm"
          className="ml-1 gap-1.5 rounded-full pl-3 pr-1.5"
        >
          <Link to="/contacts/create">
            <Plus className="size-3.5" />
            Ajouter contact
            <kbd className="ml-1 inline-flex items-center rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-mono text-white">
              N
            </kbd>
          </Link>
        </Button>
      </div>
      <ContactImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </TopToolbar>
  );
};

export const ContactListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "first_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const { isPending, data, error, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader>
        <ContactListFilter />
      </MobileHeader>
      <MobileContent>
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");

  const contacts = records.map((contact) => {
    const exportedContact = {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id].name
          : undefined,
      sales:
        contact.sales_id != null
          ? `${sales[contact.sales_id].first_name} ${sales[contact.sales_id].last_name}`
          : undefined,
      tags: contact.tags.map((tagId) => tags[tagId].name).join(", "),
      email_work: contact.email_jsonb?.find((email) => email.type === "Work")
        ?.email,
      email_home: contact.email_jsonb?.find((email) => email.type === "Home")
        ?.email,
      email_other: contact.email_jsonb?.find((email) => email.type === "Other")
        ?.email,
      email_jsonb: JSON.stringify(contact.email_jsonb),
      email_fts: undefined,
      phone_work: contact.phone_jsonb?.find((phone) => phone.type === "Work")
        ?.number,
      phone_home: contact.phone_jsonb?.find((phone) => phone.type === "Home")
        ?.number,
      phone_other: contact.phone_jsonb?.find((phone) => phone.type === "Other")
        ?.number,
      phone_jsonb: JSON.stringify(contact.phone_jsonb),
      phone_fts: undefined,
    };
    delete exportedContact.email_fts;
    delete exportedContact.phone_fts;
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contacts");
  });
};
